import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import ErrorBoundary from './components/ErrorBoundary';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe with the publishable key from environment variables
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Initialize app
const init = () => {
  const root = document.getElementById('root');
  if (!root) {
    console.error('Root element not found');
    return;
  }

  // Set up service worker message listener for notifications
  if ('serviceWorker' in navigator && !import.meta.env.DEV) {
    try {
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('Received message from service worker:', event.data);
        if (event.data && event.data.type === 'PLAY_NOTIFICATION') {
          console.log('Playing notification sound from service worker message:', event.data.sound);
          const audio = new Audio(event.data.sound);
          audio.volume = 0.5;
          audio.play().catch(err => {
            console.error('Error playing notification sound from service worker message:', err);
          });
        }
      });
      
      // Register service worker
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => {
          console.log('Service Worker registered with scope:', registration.scope);
        })
        .catch(error => {
          // Silently handle service worker registration failure in development
          if (!error.message.includes('not yet supported')) {
            console.error('Service Worker registration failed:', error);
          }
        });
    } catch (error) {
      // Silently handle service worker errors in development
      if (!error.message.includes('not yet supported')) {
        console.error('Service Worker error:', error);
      }
    }
  }

  // Create root and render app
  try {
    createRoot(root).render(
      <StrictMode>
        <ErrorBoundary>
          <Elements stripe={stripePromise}>
            <App />
          </Elements>
        </ErrorBoundary>
      </StrictMode>
    );
  } catch (error) {
    console.error('Error rendering app:', error);
    // Show error message to user
    root.innerHTML = `
      <div style="padding: 20px; text-align: center;">
        <h1>Unable to load application</h1>
        <p>Please try refreshing the page. If the problem persists, contact support.</p>
      </div>
    `;
  }
};

// Handle initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Add error boundary
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
  // Only handle errors that weren't caught by React error boundary
  if (!event.error?.message?.includes('React')) {
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `
        <div style="padding: 20px; text-align: center;">
          <h1>Something went wrong</h1>
          <p>Please try refreshing the page. If the problem persists, contact support.</p>
        </div>
      `;
    }
  }
});